import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import ReportFilters from './components/ReportFilters';
import InteractiveChart from './components/InteractiveChart';
import AchievementTimeline from './components/AchievementTimeline';
import WellnessInsights from './components/WellnessInsights';
import ComparativeAnalysis from './components/ComparativeAnalysis';
import ExportOptions from './components/ExportOptions';
import PageTransition from '../../components/PageTransition';

const ProgressReports = () => {
  const [reportFilters, setReportFilters] = useState({
    dateRange: 'last30days',
    reportType: 'monthly',
    metrics: ['steps', 'calories', 'hydration', 'sleep']
  });

  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  // Mock data for charts
  const chartData = [
    { date: 'Oct 1', steps: 8500, calories: 2100, hydration: 2.2, sleep: 7.2, weight: 70.5, mood: 8 },
    { date: 'Oct 2', steps: 12000, calories: 2400, hydration: 2.8, sleep: 6.8, weight: 70.3, mood: 7 },
    { date: 'Oct 3', steps: 9800, calories: 2200, hydration: 2.5, sleep: 7.5, weight: 70.2, mood: 8 },
    { date: 'Oct 4', steps: 11500, calories: 2350, hydration: 3.0, sleep: 6.5, weight: 70.0, mood: 6 },
    { date: 'Oct 5', steps: 13200, calories: 2500, hydration: 2.9, sleep: 7.0, weight: 69.8, mood: 9 },
    { date: 'Oct 6', steps: 10800, calories: 2300, hydration: 2.7, sleep: 6.2, weight: 69.9, mood: 7 },
    { date: 'Oct 7', steps: 14500, calories: 2600, hydration: 3.2, sleep: 7.8, weight: 69.7, mood: 9 },
    { date: 'Oct 8', steps: 12800, calories: 2450, hydration: 2.8, sleep: 6.9, weight: 69.6, mood: 8 },
    { date: 'Oct 9', steps: 11200, calories: 2380, hydration: 2.6, sleep: 7.3, weight: 69.5, mood: 8 },
    { date: 'Oct 10', steps: 13800, calories: 2520, hydration: 3.1, sleep: 6.4, weight: 69.4, mood: 7 },
    { date: 'Oct 11', steps: 12500, calories: 2400, hydration: 2.8, sleep: 6.2, weight: 69.3, mood: 8 }
  ];

  const availableMetrics = ['steps', 'calories', 'hydration', 'sleep', 'weight', 'mood'];

  const handleFiltersChange = (newFilters) => {
    setReportFilters(newFilters);
  };

  const handleGenerateReport = () => {
    console.log('Generating comprehensive report with filters:', reportFilters);
  };

  return (
    <>
      <Helmet>
        <title>Progress Reports - PrimeOne+</title>
        <meta name="description" content="Comprehensive wellness analytics and insights. Analyze long-term health trends and celebrate achievements through detailed reporting and data visualization." />
      </Helmet>
      <Header />
      <PageTransition>
        <div className="min-h-screen bg-background">
          {/* Header Section */}
          <div className="bg-card border-b border-border/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <motion.div
                className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0"
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="flex items-center space-x-4">
                  <motion.div
                    className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl"
                    whileHover={{ scale: 1.08, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <Icon name="BarChart3" size={24} className="text-primary" />
                  </motion.div>
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">Progress Reports</h1>
                    <p className="text-muted-foreground mt-1">
                      Comprehensive wellness analytics and insights for your health journey
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button variant="outline" iconName="RefreshCw" iconPosition="left">Refresh Data</Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button variant="default" onClick={handleGenerateReport} iconName="FileText" iconPosition="left">Generate Report</Button>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="space-y-8">
              {/* Report Filters */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
              >
                <ReportFilters 
                  onFiltersChange={handleFiltersChange}
                  selectedFilters={reportFilters}
                />
              </motion.div>

              {/* Interactive Charts Section */}
              <motion.div
                className="grid grid-cols-1 xl:grid-cols-2 gap-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              >
                <InteractiveChart
                  title="Activity & Fitness Trends"
                  data={chartData}
                  metrics={['steps', 'calories']}
                  chartType="line"
                />
                <InteractiveChart
                  title="Wellness Metrics Overview"
                  data={chartData}
                  metrics={['hydration', 'sleep', 'mood']}
                  chartType="area"
                />
              </motion.div>

              {/* Comparative Analysis */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25, ease: [0.4, 0, 0.2, 1] }}
              >
                <ComparativeAnalysis />
              </motion.div>

              {/* Achievement Timeline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
              >
                <AchievementTimeline />
              </motion.div>

              {/* Wellness Insights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.35, ease: [0.4, 0, 0.2, 1] }}
              >
                <WellnessInsights />
              </motion.div>

              {/* Export Options */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4, ease: [0.4, 0, 0.2, 1] }}
              >
                <ExportOptions reportFilters={reportFilters} />
              </motion.div>

              {/* Summary Statistics */}
              <motion.div
                className="bg-card rounded-xl p-6 elevation-2 border border-border/40"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.45, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg">
                    <Icon name="TrendingUp" size={20} className="text-success" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">Report Summary</h3>
                    <p className="text-sm text-muted-foreground">Key highlights from your wellness journey</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { value: '12,500', label: 'Avg Daily Steps', sub: '+23% from last month', subColor: 'text-success', color: 'text-primary' },
                    { value: '2.8L', label: 'Daily Hydration', sub: '+18% improvement', subColor: 'text-success', color: 'text-accent' },
                    { value: '6.2h', label: 'Avg Sleep', sub: '-12% needs attention', subColor: 'text-warning', color: 'text-secondary' },
                    { value: '85/100', label: 'Wellness Score', sub: '+8% this month', subColor: 'text-success', color: 'text-success' },
                  ]?.map((stat, i) => (
                    <motion.div
                      key={stat?.label}
                      className="text-center p-4 bg-muted/30 rounded-lg cursor-default"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.5 + i * 0.06 }}
                      whileHover={{ y: -3, scale: 1.02 }}
                    >
                      <div className={`text-2xl font-bold ${stat?.color} mb-1`}>{stat?.value}</div>
                      <div className="text-sm text-muted-foreground">{stat?.label}</div>
                      <div className={`text-xs mt-1 ${stat?.subColor}`}>{stat?.sub}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default ProgressReports;